import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ATSProviderWrapper } from "@/Project/src/components/ATSProviderWrapper";
import ATSLayoutWrapper from "@/Project/src/components/ATSLayoutWrapper";
import navigationRoutes from "@/utils/navigationRoutes";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  // If no tenant ID is available, show a tenant selection page
  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Please provide a tenant ID in the URL path (e.g.,
            /your-tenant-id/login)
          </p>
        </div>
      </div>
    );
  }
  const publicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const authRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const atsRoutes = routes.find(m => m.type === "ats")?.routes ?? [];
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={navigationRoutes.ATS_LOGIN} replace />}
        />
        <Route element={<TopLevelComponent />}>
          {publicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* ATS (Isolated) Routes */}
          <Route element={<ATSProviderWrapper />}>
            {(atsRoutes as any[])
              .filter(r => !r.protected)
              .map(({ path, element: Page }: any) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRouter />}>
            <Route element={<ATSProviderWrapper />}>
              {/* Render Main Project Pages inside ATS Layout when accessed via /ats */}
              <Route path="/ats" element={<ATSLayoutWrapper />}>
                {(authRoutes as any[]).map(({ path, element: Page }: any) => (
                  <Route
                    key={path}
                    path={path.startsWith("/") ? path.substring(1) : path}
                    element={<Page />}
                  />
                ))}
              </Route>

              {/* ATS Protected Routes - WITHOUT MainLayout */}
              {(atsRoutes as any[])
                .filter(r => r.protected)
                .map(({ path, element: Page }: any) => (
                  <Route key={path} path={path} element={<Page />} />
                ))}
            </Route>

            <Route element={<MainLayout />}>
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
